(ns nosco.richtext.embed
  "Namespace that deals with embedding third-party content
  inside the editor."
  (:require
   ["react" :as React]
   ["react-error-boundary" :as reb]
   [ajax.core :refer [GET json-response-format]]
   [clojure.string :as string]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.graphql-client]
   [nosco.i18n.translate :as t]
   [nosco.oembed :as nosco-oembed]
   [nosco.richtext.schema :refer [iframe-info iframe-node]]
   [nosco.ui.banners :as banners]
   [nosco.ui.html :as html]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.loading :as loading]
   [reseda.react :as reseda-react]))

(defn oembed-request
  "We have a proxy for CORS avoidance and possibilities of logging, caching etc."
  [service-url link]
  (js/Promise.
   (fn [resolve reject]
     (GET "/oembed-proxy" {:params {:format "json"
                                    :maxwidth 600
                                    :service-url service-url
                                    :url link}
                           :handler resolve
                           :error-handler reject
                           :response-format (json-response-format {:keywords? true})}))))

(defnc ResponsiveIframeContainer
  "An iframe container that maintains the aspect ratio of the child iframe,
  and can also be set to have max-width if needed.
  Note that even if the prop is any random html, the html gets parsed and only
  an iframe node is put in here, for consistency and security."
  [{:keys [html]}]
  (let [{:keys [html ratio]} (iframe-info (iframe-node html))]
    (when html
      [html/SafeHTMLEditor
       {:html html
        :tag :figure
        :class "nosco-iframe-container"
        :style {:--figure-ratio (str ratio "%")}}])))

(defnc EmbedPreview*
  "Given an oembed response, show an iframe container."
  [{:keys [oembed-response*]}]
  (let [resp (deref oembed-response*)
        {:keys [type title thumbnail_url thumbnail_width thumbnail_height
                html width height]} resp]
    [ResponsiveIframeContainer {:html html}]))

(defn insert-embed-code-field! [^js view embed-code-field-state]
  (let [{::keys [oembed-response* link-or-code]} embed-code-field-state
        schema (.. view -state -schema)
        oembed-response (and oembed-response*
                             (realized? oembed-response*)
                             (-> oembed-response* deref :html))
        ;; if we have an oembed response, use that, otherwise assume
        ;; that the link is an iframe code
        oembed-code (or oembed-response link-or-code)
        attrs (iframe-info (iframe-node oembed-code))]
    ;; assuming we have a correct iframe
    (when attrs
      (let [ratio (:ratio attrs)
            attrs (select-keys attrs nosco.richtext.schema/iframe-attrs)
            iframe-node (.node schema "iframe" (clj->js attrs))
            figure-node (.node schema "figure" #js {:data-src (:src attrs)
                                                    :class "nosco-iframe-container"
                                                    :style (str "--figure-ratio: " ratio "%;")}
                               iframe-node)
            tr (.. view -state -tr)
            tr (.replaceSelectionWith tr figure-node false)]
        ((.-dispatch view) tr))))
  (.focus view))

(def embed-code-field-initial-state
  {::link-or-code ""
   ::oembed-response* nil})

(defnc EmbedCodeField [{:keys [state on-state-change]}]
  (let [{::keys [link-or-code oembed-response*]} state]
    [:div
     [inputs/TextArea
      {:placeholder (t/t "Paste link or embed code")
       :message (t/t "Works with videos from Microsoft Stream, YouTube, Vimeo or any embed code")
       :value link-or-code
       :set-value
       (fn [new-link-or-code]
         (on-state-change
          (assoc state
                 ::link-or-code new-link-or-code
                 ::oembed-response* (some-> (nosco-oembed/oembed-service-url (string/trim new-link-or-code))
                                            (oembed-request (string/trim new-link-or-code))
                                            (reseda-react/suspending-value)))))}]
     (cond
       oembed-response*
       [React/Suspense {:fallback (hx/f [loading/LoadingNew])}
        [reb/ErrorBoundary
         {:fallback (hx/f [banners/WarningBanner
                           [banners/BannerTitle (t/t "Automatic embed not available")]
                           [banners/BannerText (t/t "Could not automatically generate an embed code, please try manually.")]])}
         [EmbedPreview* {:oembed-response* oembed-response*}]]]

       (and (nil? oembed-response*)
            (not (string/blank? link-or-code)))
       [ResponsiveIframeContainer {:html link-or-code}])]))
